import { XMarkIcon } from '@heroicons/react/24/outline'
import { PipeLineType } from 'config'
import { useEffect, useState } from 'react'
import { filterPipeline } from 'services'
import { getLoanDisplayNo } from 'utils'

export type IncludedLoan = {
  no: number
  display: string
  borrower: string
  address: string
}

const MAX_INCLUDED_LOANS = 50

const headerIndex = (headers: string[], names: string[]) =>
  headers.findIndex((header) => names.some((name) => header === name || header.endsWith(name)))

export const ReportIncludedLoans = ({
  loans,
  pipelineType,
  onChange,
}: {
  loans: IncludedLoan[]
  pipelineType: string
  onChange: (loans: IncludedLoan[]) => void
}) => {
  const [term, setTerm] = useState('')
  const [matches, setMatches] = useState<IncludedLoan[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    const query = term.trim()
    if (query.length < 2) {
      setMatches([])
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      const search = {
        query,
        skip: 0,
        count: 8,
        orderBy: 'relevance',
        orderDir: 1,
        pipelineType: pipelineType || PipeLineType.Default,
        // The report filter is what this loan is being added around. The lookup itself has to see
        // past that, including a servicing loan that is paid off or sold.
        activeOnly: false,
      }
      filterPipeline(search)
        .then((res: any) => {
          if (cancelled) return
          const headers: string[] = Array.isArray(res?.headers) ? res.headers : []
          const borrowerIdx = headerIndex(headers, ['Borrower', 'Borrower Name'])
          const addressIdx = headerIndex(headers, ['Property Address'])
          const rows: any[][] = Array.isArray(res?.data) ? res.data : []
          const extra: any[] = Array.isArray(res?.additionalData) ? res.additionalData : []
          const found: IncludedLoan[] = []
          rows.forEach((row, index) => {
            const item = extra[index] || {}
            const no = Number(item.no)
            if (!no) return
            found.push({
              no,
              display: getLoanDisplayNo({ ...item, loanNumber: no }) || String(no),
              borrower: borrowerIdx >= 0 ? String(row[borrowerIdx] || '').trim() : '',
              address: addressIdx >= 0 ? String(row[addressIdx] || '').trim() : '',
            })
          })
          setMatches(found)
        })
        .catch(() => {
          if (!cancelled) setMatches([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [term, pipelineType])

  const add = (loan: IncludedLoan) => {
    if (loans.some((item) => item.no === loan.no) || loans.length >= MAX_INCLUDED_LOANS) return
    onChange([...loans, loan])
    setTerm('')
    setMatches([])
  }

  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-gray-900">Also include these loans</p>
      <p className="mb-2 text-xs text-gray-500">
        They stay in this report even when they do not match the filters above. Up to {MAX_INCLUDED_LOANS}.
      </p>
      <div className="relative max-w-md">
        <input
          type="search"
          value={term}
          placeholder="Loan #, borrower, or address"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-shade-blue focus:outline-none"
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && matches.length === 1) {
              event.preventDefault()
              add(matches[0])
            }
          }}
        />
        {term.trim().length >= 2 && (searching || matches.length > 0) && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {searching && !matches.length ? (
              <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
            ) : (
              matches.map((loan) => {
                const added = loans.some((item) => item.no === loan.no)
                return (
                  <button
                    key={loan.no}
                    type="button"
                    disabled={added}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-default disabled:text-gray-400"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => add(loan)}
                  >
                    <span className="font-semibold text-shade-blue">{loan.display}</span>
                    {loan.borrower ? <span className="ml-2 text-gray-800">{loan.borrower}</span> : null}
                    {loan.address ? <span className="mt-0.5 block text-xs text-gray-500">{loan.address}</span> : null}
                    {added ? <span className="ml-2 text-xs text-gray-400">Added</span> : null}
                  </button>
                )
              })
            )}
          </div>
        )}
      </div>
      {loans.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {loans.map((loan) => (
            <span
              key={loan.no}
              className="inline-flex items-center gap-1 rounded-full bg-shade-blue/10 px-2.5 py-1 text-sm text-gray-800"
            >
              <span className="font-semibold text-shade-blue">{loan.display || loan.no}</span>
              {loan.borrower ? <span>{loan.borrower}</span> : null}
              <button
                type="button"
                className="text-gray-400 hover:text-red-600"
                aria-label={`Remove loan ${loan.display || loan.no}`}
                onClick={() => onChange(loans.filter((item) => item.no !== loan.no))}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
